import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { DepartmentResponse } from "@/types/department";

// Hook for fetching departments for a specific organization
// API returns complete list - filtering/sorting done client-side
export function useDepartments(organizationId: string | null) {
  const {
    data: departments = [],
    isLoading,
    error
  } = useQuery<DepartmentResponse[]>({
    queryKey: ["/organizations", organizationId, "departments"],
    queryFn: async () => {
      if (!organizationId) throw new Error("Organization ID is required");
      const response = await apiRequest("GET", `/organizations/${organizationId}/departments`);
      return response.data || response;
    },
    enabled: !!organizationId
  });

  return {
    departments,
    isLoading,
    error
  };
}

// Hook for fetching a single department
export function useDepartment(organizationId: string | null, departmentId: string | null) {
  const {
    data: department,
    isLoading,
    error
  } = useQuery<DepartmentResponse>({
    queryKey: ["/organizations", organizationId, "departments", departmentId],
    queryFn: async () => {
      if (!organizationId || !departmentId) throw new Error("Organization ID and Department ID are required");
      const response = await apiRequest("GET", `/organizations/${organizationId}/departments/${departmentId}`);
      return response.data || response;
    },
    enabled: !!organizationId && !!departmentId
  });

  return {
    department,
    isLoading,
    error
  };
}